import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from '../../app/LanguageContext';
import { formatCurrency, formatPercent } from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

function resolveSimulationQuantity({ quantity, result }) {
  const fromResult = ni(result?.detail?.quantity);
  const fromProp = ni(quantity);
  return Math.max(1, fromResult || fromProp || 1);
}

function SummaryMetric({ label, value, valueVariant, valueSx = {}, stacked = false, secondaryLabel = null }) {
  if (stacked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {label}
          </Typography>
          {secondaryLabel ? (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ lineHeight: 1.15, mt: 0.15 }}>
              {secondaryLabel}
            </Typography>
          ) : null}
        </Box>
        <Typography variant={valueVariant} fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', ...valueSx }}>
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      {secondaryLabel ? (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ lineHeight: 1.15, mb: 0.15 }}>
          {secondaryLabel}
        </Typography>
      ) : null}
      <Typography variant={valueVariant} fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', ...valueSx }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function SimulationResultSummary({ result, quantity = 1, compact = false, stacked = false }) {
  const { translate } = useTranslation();

  if (!result) return null;

  const q = resolveSimulationQuantity({ quantity, result });
  const hasSellingPrice = nf(result.pricing.selling_price) > 0;
  const minimumPrice = nf(result.pricing.minimum_selling_price ?? result.pricing.final_price);
  const effectivePrice = nf(result.pricing.effective_selling_price ?? result.pricing.final_price);
  const totalCost = nf(result.composition.subtotal_before_marketing);
  const totalProfit = nf(result.profit_margin.amount);
  const profitPercent = result.profit_margin.is_implied
    ? nf(result.profit_margin.percent)
    : nf(result.pricing.margin_pct_on_final);

  const unitCost = q > 0 ? totalCost / q : totalCost;
  const unitProfit = q > 0 ? totalProfit / q : totalProfit;
  const minimumUnitPrice = nf(result.pricing.minimum_unit_price) || (q > 0 ? minimumPrice / q : minimumPrice);
  const effectiveUnitPrice = nf(result.pricing.unit_price) || (q > 0 ? effectivePrice / q : effectivePrice);

  const valueVariant = compact ? 'body2' : 'subtitle1';
  const unitValueVariant = compact ? 'body2' : 'subtitle2';
  const rowSpacing = stacked ? 0.75 : compact ? 1 : 2;

  const profitLabel = hasSellingPrice
    ? translate('price_simulate.implied_profit')
    : translate('price_simulate.summary_fee_profit');

  const priceLabel = hasSellingPrice
    ? translate('price_simulate.selling_price')
    : translate('price_simulate.minimum_selling_price');

  const unitPriceLabel = hasSellingPrice
    ? translate('price_simulate.selling_price_unit')
    : translate('price_simulate.minimum_unit_price');

  const minimumSecondary = hasSellingPrice
    ? `${translate('price_simulate.minimum_selling_price')}: ${formatCurrency(minimumPrice)}`
    : null;

  const minimumUnitSecondary = hasSellingPrice
    ? `${translate('price_simulate.minimum_unit_price')}: ${formatCurrency(minimumUnitPrice)}`
    : null;

  const totalsRow = (
    <>
      <SummaryMetric
        stacked={stacked}
        label={translate('price_simulate.summary_footer_cost')}
        value={formatCurrency(totalCost)}
        valueVariant={valueVariant}
      />
      <SummaryMetric
        stacked={stacked}
        label={`${profitLabel} (${formatPercent(profitPercent)}%)`}
        value={formatCurrency(totalProfit)}
        valueVariant={valueVariant}
        valueSx={{ color: 'text.secondary' }}
      />
      <SummaryMetric
        stacked={stacked}
        label={priceLabel}
        value={formatCurrency(hasSellingPrice ? effectivePrice : minimumPrice)}
        valueVariant={compact ? 'subtitle1' : 'h5'}
        valueSx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}
        secondaryLabel={minimumSecondary}
      />
    </>
  );

  const unitsRow = (
    <>
      <SummaryMetric
        stacked={stacked}
        label={translate('price_simulate.summary_unit_cost')}
        value={formatCurrency(unitCost)}
        valueVariant={unitValueVariant}
      />
      <SummaryMetric
        stacked={stacked}
        label={translate('price_simulate.summary_unit_profit')}
        value={formatCurrency(unitProfit)}
        valueVariant={unitValueVariant}
        valueSx={{ color: 'text.secondary' }}
      />
      <SummaryMetric
        stacked={stacked}
        label={unitPriceLabel}
        value={formatCurrency(hasSellingPrice ? effectiveUnitPrice : minimumUnitPrice)}
        valueVariant={unitValueVariant}
        valueSx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}
        secondaryLabel={minimumUnitSecondary}
      />
    </>
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'primary.main',
        borderRadius: compact ? 1 : 1.5,
        p: compact ? 1 : 1.5,
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)'),
        width: '100%',
      }}
    >
      {stacked ? (
        <Stack spacing={rowSpacing}>{totalsRow}</Stack>
      ) : (
        <Stack direction="row" spacing={rowSpacing} justifyContent="space-between" flexWrap="wrap" gap={1}>
          {totalsRow}
        </Stack>
      )}

      {stacked ? (
        <Stack spacing={rowSpacing} sx={{ mt: compact ? 0.75 : 1, pt: compact ? 0.75 : 1, borderTop: 1, borderColor: 'divider' }}>
          {unitsRow}
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={rowSpacing}
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{
            mt: compact ? 0.75 : 1,
            pt: compact ? 0.75 : 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {unitsRow}
        </Stack>
      )}
    </Box>
  );
}
